import React, { useState } from 'react';

import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightArrowLeft, faExclamation, faCircleDashed, faClock } from '@fortawesome/pro-solid-svg-icons';

import Tooltip from 'components/tooltip';

import QuickbooksSelectModal from '../select/modal';
import type { QuickbooksEntity } from '../types';

import './styles.scss';

interface Props {
  className?: string;
  connected: boolean;
  isSetup?: boolean;
  pending?: boolean;
  type: 'customer' | 'vendor' | 'record';
  tooltipText?: string;
  buildoutObject?: Record<string, unknown>;
  quickbooksId?: number;
  onChange?: (value: QuickbooksEntity | null) => void | Promise<void>;
}

// Small badge showing whether Buildout record is linked to its QuickBooks counterpart. Payers map
// to QB Customers, outside-commission payees map to QB Vendors.
export default function QuickbooksStatusIcon({
  className, connected, isSetup = true, pending = false, type, tooltipText, buildoutObject, quickbooksId, onChange,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  let tooltip = connected
    ? 'Connected to QuickBooks'
    : `Not connected to a QuickBooks ${type === 'customer' ? 'Customer' : 'Vendor'}`;

  let icon = connected ? faArrowRightArrowLeft : faExclamation;

  if (type === 'record' && !connected && !isSetup) {
    icon = pending ? faClock : faCircleDashed;
    tooltip = 'Not synced with QuickBooks';
  }

  // Override tooltip text if provided
  if (tooltipText) {
    tooltip = tooltipText;
  }

  // Clicking opens a modal to link/create the matching QuickBooks entity. Only the customer/vendor
  // types are linkable, and only when a caller wires up onChange; otherwise the badge is read-only.
  const interactive = (type === 'customer' || type === 'vendor') && Boolean(onChange);

  const badge = (
    <Tooltip content={tooltip} dark placement="top">
      <span
        className={classNames(
          'qb-status-icon',
          {
            'qb-status-icon--connected': connected,
            'qb-status-icon--pending': !connected && pending,
            'qb-status-icon--disconnected': !connected && !pending,
          },
          interactive && 'clickable',
          className,
        )}
        {...(interactive && {
          role: 'button',
          tabIndex: 0,
          onClick: () => setIsOpen(true),
          onKeyDown: (e: React.KeyboardEvent<HTMLSpanElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(true);
            }
          },
        })}
      >
        <FontAwesomeIcon icon={icon} />
      </span>
    </Tooltip>
  );

  if (!interactive) {
    return badge;
  }

  return (
    <>
      {badge}
      <QuickbooksSelectModal
        buildoutObject={buildoutObject}
        open={isOpen}
        quickbooksId={quickbooksId}
        type={type}
        onChange={onChange!}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}